import React, { useState, useEffect, useRef, useCallback } from 'react';

const FaqModal = ({ isOpen, onClose, activeApi }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [openQuestion, setOpenQuestion] = useState(null);
  const [faqData, setFaqData] = useState([]);
  const searchInputRef = useRef(null);
  const modalRef = useRef(null);

  const handleClose = useCallback(() => {
    setSearchTerm('');
    setOpenQuestion(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      activeApi.getFaqs().then(setFaqData);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    } else {
      setSearchTerm('');
      setOpenQuestion(null);
    }
  }, [isOpen, activeApi]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
      if (event.key === 'Tab') {
        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            event.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            event.preventDefault();
          }
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose]);


  if (!isOpen) return null;

  const filteredFaq = faqData.filter(item =>
    item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleQuestion = (index) => {
    if (openQuestion === index) {
      setOpenQuestion(null);
    } else {
      setOpenQuestion(index);
    }
  };

  const handleQuestionKeyDown = (event, index) => {
    if (event.key === 'Enter' || event.key === ' ') {
      toggleQuestion(index);
    }
    if (event.key === 'ArrowDown') {
        event.preventDefault();
        const nextQuestion = event.target.closest('.faq-item').nextElementSibling;
        if (nextQuestion) {
            nextQuestion.querySelector('.section-header').focus();
        }
    }
    if (event.key === 'ArrowUp') {
        event.preventDefault();
        const prevQuestion = event.target.closest('.faq-item').previousElementSibling;
        if (prevQuestion) {
            prevQuestion.querySelector('.section-header').focus();
        }
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" ref={modalRef}>
        <div className="modal-header">
          <h2><i className="material-icons">help_outline</i>Frequently Asked Questions</h2>
          <button title="Close" onClick={handleClose} className="icon-button close-button"><i className="material-icons">close</i></button>
        </div>
        <div className="modal-body">
        <div className="search-input-container">
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search FAQ..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && <button title="Clear search" onClick={() => setSearchTerm('')} className="clear-input-button"><i className="material-icons">close</i></button>}
        </div>
        <div className="faq-list">
          {filteredFaq.map((item, index) => (
            <div key={index} className="collapsible-section faq-item">
              <div 
                className="section-header" 
                onClick={() => toggleQuestion(index)}
                onKeyDown={(e) => handleQuestionKeyDown(e, index)}
                tabIndex={0}
              >
                <h3>{item.question}</h3>
                <i className="material-icons">{openQuestion === index ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</i>
              </div>
              {openQuestion === index && (
                <div className="faq-answer">
                  <p>{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
        </div>
      </div>
    </div>
  );
};

export default FaqModal;